// src/MuralPage.js
import React, { useEffect, useState, useRef } from "react";
import { db, storage } from "./firebase";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  updateDoc,
  getDoc,
  onSnapshot,
  setDoc,
  query,
  where,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import CanvasDraw from "react-canvas-draw";
import MuralUploader from "./MuralUploader";
import "./App.css";
import html2canvas from "html2canvas";
import { HexColorPicker } from "react-colorful";

const LOGO_URL = "https://firebasestorage.googleapis.com/v0/b/community-squares-766d6.firebasestorage.app/o/footer%2F46C9DCB8-6230-4B21-8F24-001B0F4C3CA9.PNG?alt=media&token=aade42b3-a937-4cf2-adf1-09fb926fdb36";
const USE_PRESET_COLORS = true; // flip to false to go back to full HexColorPicker mode

const PRESET_COLORS = [
  "#C2411F",
  "#E0623C",
  "#E6C29A",
  "#FFF3E6",
  "#0F7C78",
  "#141414",
];

// 🔌 Feature flags – turn each rule on/off
const LIMIT_SUBMISSIONS_24H = true;   // limit claims in a rolling 24h window
const LIMIT_MURAL_SHARE    = true;    // limit total % of mural a user can submit

// 🔢 Limits
const MAX_CLAIMS_24H       = 5;       // max CLAIMS per user / 24h
const MAX_PCT_PER_MURAL    = 0.15;    // 15% of total squares in this mural

/**
 * Enforces:
 *  - 24h claim limit (if LIMIT_SUBMISSIONS_24H === true) using claimedAt
 *  - max % of a mural (if LIMIT_MURAL_SHARE === true) using submittedBy
 *  - admin bypass (isAdmin === true ignores limits)
 *
 * @param {Object} params
 * @param {Array}  params.squares  - squares for the *active mural* only
 * @param {string} params.uid      - current user's uid
 * @param {boolean} params.isAdmin - whether current user is admin
 *
 * @returns {boolean} true if user is allowed to claim another square
 */
const canUserClaimOnActiveMural = ({ squares, uid, isAdmin }) => {
  if (!uid) return false;

  // 👑 Admin bypass – admins ignore all limits
  if (isAdmin) return true;

  // If both rules are off, always allow
  if (!LIMIT_SUBMISSIONS_24H && !LIMIT_MURAL_SHARE) return true;

  if (!Array.isArray(squares) || squares.length === 0) return true;

  const nowMs    = Date.now();
  const cutoffMs = nowMs - 24 * 60 * 60 * 1000; // last 24 hours

  let recentClaims24h     = 0; // how many this user CLAIMED in last 24h (this mural)
  let userSubmissionsTotal = 0; // how many this user SUBMITTED total (this mural)

  squares.forEach((sq) => {
    if (!sq) return;

    // Count submissions using submittedBy
    if (sq.submittedBy === uid) {
      userSubmissionsTotal++;
    }

    // Count claims in last 24h using claimedBy + claimedAt
    if (sq.claimedBy === uid && sq.claimedAt) {
      let claimedMs;

      if (typeof sq.claimedAt?.toMillis === "function") {
        // Firestore Timestamp
        claimedMs = sq.claimedAt.toMillis();
      } else if (typeof sq.claimedAt === "string") {
        // ISO string
        claimedMs = Date.parse(sq.claimedAt);
      } else if (typeof sq.claimedAt === "number") {
        // stored as ms (Date.now())
        claimedMs = sq.claimedAt;
      }

      if (!Number.isNaN(claimedMs) && claimedMs >= cutoffMs) {
        recentClaims24h++;
      }
    }
  });

  const totalSquares   = squares.length;
  const maxUserSquares = Math.floor(totalSquares * MAX_PCT_PER_MURAL);

  // ⏱️ Rule 1: 24-hour claim cap (based on claimedAt)
  if (LIMIT_SUBMISSIONS_24H && recentClaims24h >= MAX_CLAIMS_24H) {
    alert(
      "You’ve already claimed 5 patches in the last 24 hours. " +
      "Thanks for contributing — take a little break before doing more 🧡"
    );
    return false;
  }

  // 📊 Rule 2: max % of mural (based on submittedBy)
  if (
    LIMIT_MURAL_SHARE &&
    maxUserSquares >= 1 &&
    userSubmissionsTotal > maxUserSquares // “over 15%” => strictly greater
  ) {
    alert(
      "You’ve already contributed your share of this mural. " +
      "It’s looking amazing — now let others help fill it in 💛"
    );
    return false;
  }

  return true;
};



function MuralPage({ isAdmin, setIsAdmin }) {
 const [squares, setSquares] = useState([]);
  const [gridCols, setGridCols] = useState(4);
  const [gridRows, setGridRows] = useState(5);
  const [uid, setUid] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [claimedSquare, setClaimedSquare] = useState(null);
  const [pendingUsername, setPendingUsername] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [clue, setClue] = useState("");
  const [answer, setAnswer] = useState("");
  const [brushColor, setBrushColor] = useState("#000000");
  const [brushRadius, setBrushRadius] = useState(4);
  const [darkness, setDarkness] = useState(1); // right = darker
  const [softness, setSoftness] = useState(1);
  const [isErasing, setIsErasing] = useState(false);
  const [username, setUsername] = useState("");
  
  const [timeLeft, setTimeLeft] = useState(null);
  const [submittedBy, setSubmittedBy] = useState(null);
  const canvasRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [visibleSquares, setVisibleSquares] = useState([]);
  const [userHasSubmitted, setUserHasSubmitted] = useState(false);
  const [isMuralComplete, setIsMuralComplete] = useState(false);
const [selectedPresetIndex, setSelectedPresetIndex] = useState(0);
// each preset gets its own shade value (0–100)


// ✅ Detect admin via Firebase Auth claim
useEffect(() => {
  const auth = getAuth();
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      const token = await user.getIdTokenResult(true);
      setIsAdmin(!!token.claims.admin);
    } else {
      setIsAdmin(false);
    }
  });
  return unsubscribe;
}, []);





  // Session + Firestore listeners
  // Session listener + preload username
useEffect(() => {
  const auth = getAuth();

  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    if (!user) {
      signInAnonymously(auth).catch((err) =>
        console.error("Anon sign-in failed:", err)
      );
    } else {
      setUid(user.uid);

      // preload username if set
      (async () => {
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            const savedName = snap.data().username || "";
            setUsername(savedName);
            setPendingUsername(savedName);
          }
        } catch (err) {
          console.error("Error fetching username:", err);
        }
      })();
    }
  });

  return () => {
    unsubscribeAuth();
  };
}, []);

// Active mural + squares + leaderboard + clue + grid
useEffect(() => {
  // If we don't have a uid yet, just clear mural-related state and bail
  if (!uid) {
    setSquares([]);
    setUserHasSubmitted(false);
    setIsMuralComplete(false);
    setClaimedSquare(null);
    setLeaderboard([]);
    return;
  }

  const activeRef = doc(db, "gameSettings", "activeMural");
  let unsubscribeSquares = () => {};

  // Listen to which mural is active
  const unsubscribeActive = onSnapshot(activeRef, (activeSnap) => {
    const activeData = activeSnap.data();
    const muralId = activeData?.muralId;

    // If no active mural yet, clear UI and stop
    if (!muralId) {
      setSquares([]);
      setUserHasSubmitted(false);
      setIsMuralComplete(false);
      setClaimedSquare(null);
      setLeaderboard([]);
      unsubscribeSquares();
      return;
    }

    // If we already had a squares listener for a previous mural, clean it up
    unsubscribeSquares();

    // Query only the squares for the active mural
    const q = query(
      collection(db, "squares"),
      where("muralId", "==", muralId)
    );

    unsubscribeSquares = onSnapshot(q, async (snapshot) => {
      const now = Date.now();
      const expiryHours = 4 * 60 * 60 * 1000;

      const sqs = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Expiry check
        if (
          data.claimed &&
          data.claimedAt &&
          now - data.claimedAt > expiryHours &&
          !data.uploadedDrawingUrl
        ) {
          await updateDoc(doc(db, "squares", docSnap.id), {
            claimed: false,
            claimedBy: "",
            submittedBy: "",
            claimedAt: null,
          });
        }

        sqs.push({ id: docSnap.id, ...data });
      }

      // Sort squares in grid order
      sqs.sort((a, b) =>
        a.row === b.row ? a.col - b.col : a.row - b.row
      );

      // Update state
      setSquares(sqs);

      // ✅ Has this user submitted?
      const submittedSquares = sqs.filter(
        (sq) => sq.submittedBy === uid && sq.uploadedDrawingUrl
      );
      setUserHasSubmitted(submittedSquares.length > 0);

      // ✅ Is the mural complete?
      setIsMuralComplete(sqs.every((sq) => sq.uploadedDrawingUrl));

      // ✅ Your currently claimed (but not yet uploaded) square
      const mySquare = sqs.find(
        (sq) => sq.claimedBy === uid && !sq.uploadedDrawingUrl
      );
      setClaimedSquare(mySquare || null);

      // 🔹 Build submissions map for leaderboard
      const submissions = {};
      sqs.forEach((sq) => {
        if (sq.submittedBy) {
          submissions[sq.submittedBy] =
            (submissions[sq.submittedBy] || 0) + 1;
        }
      });

      // 🔹 Build leaderboard with usernames
      const results = [];
      for (const userId of Object.keys(submissions)) {
        try {
          const snapUser = await getDoc(doc(db, "users", userId));
          const uname = snapUser.exists()
            ? snapUser.data().username
            : "Unknown";
          results.push({ username: uname, count: submissions[userId] });
        } catch (err) {
          results.push({ username: "Unknown", count: submissions[userId] });
        }
      }
      results.sort((a, b) => b.count - a.count);
      setLeaderboard(results);
    });
  });

  // 🔹 Fetch clue (once per effect run)
  const fetchClue = async () => {
    const clueRef = doc(db, "gameSettings", "clues");
    const clueSnap = await getDoc(clueRef);
    if (clueSnap.exists()) {
      const data = clueSnap.data();
      setClue(data.currentClue);
      setAnswer(data.currentAnswer || "");
    }
  };

  // 🔹 Fetch grid settings (once per effect run)
  const fetchGridSettings = async () => {
    try {
      const settingsRef = doc(db, "gameSettings", "grid");
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        console.log("✅ Grid settings fetched:", data);
        setGridCols(data.cols || 8);
        setGridRows(data.rows || 10);
      } else {
        console.warn(
          "⚠️ No grid settings found in Firestore — using fallback 8x10"
        );
        setGridCols(8);
        setGridRows(10);
      }
    } catch (err) {
      console.error("⚠️ Error fetching grid settings:", err);
      setGridCols(8);
      setGridRows(10);
    }
  };

  fetchClue();
  fetchGridSettings();

  // Cleanup active mural + squares listeners when uid changes or component unmounts
  return () => {
    unsubscribeActive();
    unsubscribeSquares();
  };
}, [uid]);


  // Countdown timer
  useEffect(() => {
    if (!claimedSquare?.claimedAt) {
      setTimeLeft(null);
      return;
    }
    const interval = setInterval(() => {
      const now = Date.now();
      const expiry = claimedSquare.claimedAt + 4 * 60 * 60 * 1000;
      const remaining = expiry - now;
      if (remaining <= 0) {
        setTimeLeft("Expired");
      } else {
        const mins = Math.floor(remaining / 60000);
        const secs = Math.floor((remaining % 60000) / 1000);
        setTimeLeft(`${mins}:${secs.toString().padStart(2, "0")}`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [claimedSquare]);

  // In-App Auto-Reset
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = Date.now();
      const expiryHours = 4 * 60 * 60 * 1000;

      for (const sq of squares) {
        if (
          sq.claimed &&
          sq.claimedAt &&
          now - sq.claimedAt > expiryHours &&
          !sq.uploadedDrawingUrl
        ) {
          await updateDoc(doc(db, "squares", sq.id), {
            claimed: false,
            claimedBy: "",
            submittedBy: "",
            claimedAt: null,
          });
          console.log(`Square ${sq.id} auto-reset after x hours`);
        }
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [squares]);

// ✅ Save completed mural to gallery (admin only)
const saveMuralToGallery = async () => {
  if (!isAdmin) {
    alert("❌ You must be an admin to save the mural.");
    return;
  }

  if (!squares.every((sq) => sq.uploadedDrawingUrl)) {
    alert("❌ Mural is not complete yet!");
    return;
  }

  // Wait to ensure all squares render
  await new Promise((res) => setTimeout(res, 500));

  const muralElement = document.getElementById("mural-container");
  if (!muralElement) {
    alert("❌ Mural not found!");
    return;
  }

  // Wait for all images to fully load
  const images = muralElement.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalHeight !== 0) resolve();
          else img.onload = img.onerror = () => resolve();
        })
    )
  );

  console.log("✅ All mural images loaded — capturing full mural...");

  // Temporarily remove height limit and overflow
  const prevMaxHeight = muralElement.style.maxHeight;
  const prevOverflow = muralElement.style.overflow;
  muralElement.style.maxHeight = "none";
  muralElement.style.overflow = "visible";

  // Hide buttons temporarily
  const buttons = muralElement.querySelectorAll("button");
  buttons.forEach((b) => (b.style.display = "none"));

  // Capture full mural with html2canvas
  const baseCanvas = await html2canvas(muralElement, {
    scale: 3,
    useCORS: true,
    backgroundColor: null,
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: muralElement.scrollWidth,
    windowHeight: muralElement.scrollHeight,
  });

  // 🟦 Frame / border settings
  const scale = 3; // same scale
  const borderThickness = 8 * scale; // blue outer border
  const innerPadding = 4 * scale; // white mat

  // 1️⃣ Create a new larger canvas to hold mural + padding + border
  const borderedCanvas = document.createElement("canvas");
  borderedCanvas.width =
    baseCanvas.width + (borderThickness + innerPadding) * 2;
  borderedCanvas.height =
    baseCanvas.height + (borderThickness + innerPadding) * 2;
  const ctx = borderedCanvas.getContext("2d");

  // 2️⃣ Blue outer border
  ctx.fillStyle = "#1e3a8a";
  ctx.fillRect(0, 0, borderedCanvas.width, borderedCanvas.height);

  // 3️⃣ White mat inside
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(
    borderThickness,
    borderThickness,
    borderedCanvas.width - borderThickness * 2,
    borderedCanvas.height - borderThickness * 2
  );

  // 4️⃣ Draw mural centered
  ctx.drawImage(
    baseCanvas,
    borderThickness + innerPadding,
    borderThickness + innerPadding
  );

  // 5️⃣ Optional crisp blue outline
  ctx.lineWidth = 1 * scale;
  ctx.strokeStyle = "#1e3a8a";
  ctx.strokeRect(
    ctx.lineWidth / 2,
    ctx.lineWidth / 2,
    borderedCanvas.width - ctx.lineWidth,
    borderedCanvas.height - ctx.lineWidth
  );

  // ✅ Restore styles on mural page
  buttons.forEach((b) => (b.style.display = ""));
  muralElement.style.maxHeight = prevMaxHeight;
  muralElement.style.overflow = prevOverflow;

  // 🔹 6️⃣ HIGH-RES VERSION (no logo) — for your own use
  const fullResBlob = await new Promise((res) =>
    borderedCanvas.toBlob(res, "image/png")
  );

  if (!fullResBlob) {
    alert("❌ Snapshot failed (no full-res blob).");
    return;
  }

  // 🔹 7️⃣ Create LOWER-RES, WATERMARKED version for gallery
  const maxDisplayWidth = 1200; // adjust if you want smaller/bigger gallery images
  const downscale =
    borderedCanvas.width > maxDisplayWidth
      ? maxDisplayWidth / borderedCanvas.width
      : 1;

  const galleryCanvas = document.createElement("canvas");
  galleryCanvas.width = Math.round(borderedCanvas.width * downscale);
  galleryCanvas.height = Math.round(borderedCanvas.height * downscale);
  const gctx = galleryCanvas.getContext("2d");

  // Draw scaled-down mural with frame
  gctx.drawImage(
    borderedCanvas,
    0,
    0,
    galleryCanvas.width,
    galleryCanvas.height
  );

  // Load logo image for watermark
  const logoImg = new Image();
  logoImg.crossOrigin = "anonymous";
  logoImg.src = LOGO_URL;

  await new Promise((resolve) => {
    if (logoImg.complete && logoImg.naturalWidth) resolve();
    else logoImg.onload = logoImg.onerror = () => resolve();
  });

  // Draw logo bottom-right if it loaded
  if (logoImg.naturalWidth && logoImg.naturalHeight) {
    const padding = galleryCanvas.width * 0.008; //
    const logoWidth = galleryCanvas.width * 0.10; // 18% of width
    const logoHeight =
      (logoWidth * logoImg.naturalHeight) / logoImg.naturalWidth;

    gctx.globalAlpha = 0.75; // slightly see-through
    gctx.drawImage(
      logoImg,
      galleryCanvas.width - logoWidth - padding,
      galleryCanvas.height - logoHeight - padding,
      logoWidth,
      logoHeight
    );
    gctx.globalAlpha = 1;
  }

  const galleryBlob = await new Promise((res) =>
    galleryCanvas.toBlob(res, "image/png")
  );

  if (!galleryBlob) {
    alert("❌ Snapshot failed (no gallery blob).");
    return;
  }

  // 🔹 8️⃣ Upload both versions to Firebase Storage
  const timestamp = Date.now();

  // Full-res (no logo) — not shown in gallery
  const fullRef = ref(storage, `gallery/muralfullres-${timestamp}.png`);
  await uploadBytes(fullRef, fullResBlob);
  const fullResUrl = await getDownloadURL(fullRef);

   // Gallery version (low-res + logo)
  const galleryRef = ref(storage, `gallery/mural-${timestamp}.png`);
  await uploadBytes(galleryRef, galleryBlob);
  const galleryUrl = await getDownloadURL(galleryRef);

  // 🔹 Compute artist stats for this mural
  const artistCounts = {};
  squares.forEach((sq) => {
    if (sq.submittedBy) {
      artistCounts[sq.submittedBy] =
        (artistCounts[sq.submittedBy] || 0) + 1;
    }
  });

  const artistsWithNames = [];
  for (const [uid, count] of Object.entries(artistCounts)) {
    const snap = await getDoc(doc(db, "users", uid));
    const uname = snap.exists()
      ? snap.data().username || "Unknown"
      : "Unknown";
    artistsWithNames.push({ username: uname, count });
  }

  artistsWithNames.sort((a, b) => b.count - a.count);

  // 🔹 9️⃣ Save metadata to Firestore (with artist stats)
  await addDoc(collection(db, "gallery"), {
    displayUrl: galleryUrl,   // used in UI gallery
    imageUrl: galleryUrl,     // optional: keeps old code working
    fullResUrl: fullResUrl,   // kept for prints / downloads later
    clue: clue || "",
    answer: answer || "",
    createdAt: serverTimestamp(),
    completedAt: Date.now(),
    artists: artistsWithNames,
  });


  alert("✅ Mural saved to gallery!");
};





// Claim
const claimSquare = async (squareId) => {
  if (!uid) {
    alert("Not signed in yet. Please wait a moment and try again.");
    return;
  }

  if (claimedSquare) {
    alert("Finish your current patch before claiming another!");
    return;
  }

  // 🚫 NEW: limits + admin bypass
  const canClaim = canUserClaimOnActiveMural({
    squares,
    uid,
    isAdmin, // make sure you have this boolean in state/props
  });

  if (!canClaim) return;

  // ✅ If all checks pass, proceed with normal claim
  await updateDoc(doc(db, "squares", squareId), {
    claimed: true,
    claimedBy: uid,
    claimedAt: Date.now(), // you already do this
  });

  setSubmittedBy(null);
};


  /* -------------------------------
     File upload (currently disabled)
     If you re-enable, keep the uid+users setDoc like the canvas submit below.
  ----------------------------------
  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);
  const uploadDrawingFile = async (squareId) => {
    if (!username) {
      alert("❌ Please enter a username before submitting.");
      return;
    }
    if (!selectedFile) {
      alert("❌ Please select an image before submitting.");
      return;
    }
    try {
      await setDoc(doc(db, "users", uid), { username }, { merge: true });
      const storageRef = ref(storage, `drawings/${squareId}.png`);
      await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(storageRef);
      await updateDoc(doc(db, "squares", squareId), {
        uploadedDrawingUrl: downloadURL,
        submittedBy: uid,
      });
      setClaimedSquare(null);
      setSelectedFile(null);
      alert("✅ Image uploaded!");
    } catch (err) {
      console.error("Upload failed:", err);
      alert("❌ Upload failed.");
    }
  };
  */

  // Canvas submit
  const submitCanvasDrawing = async (squareId) => {
    if (!uid) {
      alert("❌ Not signed in yet. Please wait a moment and try again.");
      return;
    }
    if (!username) {
      alert("❌ Please enter a username before submitting.");
      return;
    }
    if (!canvasRef.current) return;
    try {
      const dataUrl = canvasRef.current.getDataURL("png");
      const blob = await (await fetch(dataUrl)).blob();
      const storageRef = ref(storage, `drawings/${squareId}.png`);
      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);

      // save/merge username for this uid
      await setDoc(doc(db, "users", uid), { username }, { merge: true });

      await updateDoc(doc(db, "squares", squareId), {
        uploadedDrawingUrl: downloadURL,
        submittedBy: uid,
      });
      setClaimedSquare(null);
      alert("✅ Drawing submitted!");
    } catch (err) {
      console.error("Drawing upload failed:", err);
      alert("❌ Failed to submit drawing.");
    }
  };

  // Reset (admin)
  const resetSquare = async (sqId) => {
    await updateDoc(doc(db, "squares", sqId), {
      claimed: false,
      claimedBy: "",
      submittedBy: "",
      claimedAt: null,
      uploadedDrawingUrl: "",
    });
    alert(`Square ${sqId} reset!`);
    setSubmittedBy(null);
  };

  const confirmAndResetSquare = async (sqId) => {
  const ok = window.confirm("Are you sure you want to reset this square?");
  if (!ok) return;

  await resetSquare(sqId);
};


  {/*const shareMuralStatus = async () => {
    try {
      const muralElement = document.getElementById("mural-container");
      if (!muralElement) {
        alert("❌ Could not find mural element.");
        return;
      }

      const canvas = await html2canvas(muralElement, { scale: 2 });
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );

      const storageRef = ref(
        storage,
        `mural_screenshots/mural-${Date.now()}.png`
      );
      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);

      if (navigator.share) {
        await navigator.share({
          title: "Mural Status",
          text: "Check out our mural progress!",
          url: downloadURL,
        });
      } else {
        await navigator.clipboard.writeText(downloadURL);
        alert(
          "✅ Mural screenshot uploaded! Link copied to clipboard:\n" +
            downloadURL
        );
      }
    } catch (err) {
      console.error("Screenshot failed:", err);
      alert("❌ Failed to capture mural.");
    }
  };*/}

  const playAnimation = () => {
    setIsAnimating(true);
    setVisibleSquares([]); // clear all first

    setTimeout(() => {
      const sortedSquares = [...squares]
        .filter((sq) => sq.uploadedDrawingUrl)
        .sort((a, b) => (a.claimedAt || 0) - (b.claimedAt || 0));

      const totalDuration = (sortedSquares.length / 20) * 3500; // 3.5s per 20 squares
      const interval = totalDuration / sortedSquares.length;

      sortedSquares.forEach((sq, idx) => {
        setTimeout(() => {
          setVisibleSquares((prev) => [...prev, sq.id]);
          if (idx === sortedSquares.length - 1) {
            setTimeout(() => setIsAnimating(false), 1000);
          }
        }, idx * interval);
      });
    }, 0);
  };

const handleShareMyProgress = async () => {
  try {
    const muralElement = document.getElementById("mural-container");
    if (!muralElement) {
      console.warn("Mural element not found — aborting share.");
      return;
    }

    // ✅ Clone mural section (no scaling yet)
    const muralClone = muralElement.cloneNode(true);
    muralClone.querySelectorAll("button").forEach((btn) => (btn.style.display = "none")); // hide buttons

    // ✅ Build 1080x1920 canvas layout
    const wrapper = document.createElement("div");
    wrapper.style.width = "1080px";
    wrapper.style.height = "1920px";
    wrapper.style.backgroundColor = "#e6f0ff";
    wrapper.style.border = "6px solid #1e3a8a";
    wrapper.style.display = "flex";
    wrapper.style.flexDirection = "column";
    wrapper.style.alignItems = "center";
    wrapper.style.paddingBottom = "50px";
    wrapper.style.paddingTop = "160px";
    wrapper.style.textAlign = "center";
    wrapper.style.justifyContent = "center";
    wrapper.style.fontFamily = "sans-serif";
    wrapper.style.boxSizing = "border-box";
    wrapper.style.overflow = "hidden";


    // ✅ Progress bar if not complete
    const totalSquares = squares.length;
    const completedSquares = squares.filter((sq) => sq.uploadedDrawingUrl).length;
    const percent = Math.floor((completedSquares / totalSquares) * 100);

    if (percent < 100) {
      const label = document.createElement("p");
      label.textContent = `${percent}% complete — help finish it!`;
      label.style.fontSize = "45px";
      label.style.color = "#1e3a8a";
      label.style.fontWeight = "600";
      label.style.margin = "10px 0 10px 0";
      wrapper.appendChild(label);

      const bar = document.createElement("div");
      bar.style.width = "65%";
      bar.style.height = "22px";
      bar.style.backgroundColor = "#e5e7eb";
      bar.style.border = "2px solid #2563eb";
      bar.style.borderRadius = "10px";
      bar.style.margin = "0 auto 10px auto";
      bar.style.overflow = "hidden";

      const fill = document.createElement("div");
      fill.style.width = `${percent}%`;
      fill.style.height = "100%";
      fill.style.backgroundColor = "#2563eb";

      bar.appendChild(fill);
      wrapper.appendChild(bar);
    }

        // ✅ Header
    const header = document.createElement("div");
    header.innerHTML = `
      <p style="font-size:35px; color:#333; margin:20px 0 30px 0;">
        <strong>Clue:</strong> ${clue || ""}
      </p>
    `;
    wrapper.appendChild(header);

    // ✅ Create mural container centered with auto-scale logic
    const muralWrapper = document.createElement("div");
    muralWrapper.style.display = "flex";
    muralWrapper.style.justifyContent = "center";
    muralWrapper.style.alignItems = "center";
    muralWrapper.style.flex = "1";
    muralWrapper.style.maxWidth = "92%";
    muralWrapper.style.maxHeight = "80%";
    muralWrapper.style.overflow = "hidden";
    muralWrapper.appendChild(muralClone);
    wrapper.appendChild(muralWrapper);

    // ✅ Footer with logo + CTA
    const footer = document.createElement("div");
    footer.innerHTML = `
      <div style="text-align:center; margin-top:30px; line-height:1.4;">
        <img 
          src="https://firebasestorage.googleapis.com/v0/b/community-squares-766d6.firebasestorage.app/o/footer%2F46C9DCB8-6230-4B21-8F24-001B0F4C3CA9.PNG?alt=media&token=aade42b3-a937-4cf2-adf1-09fb926fdb36" 
          alt="Mural Patch" 
          style="width:100px; margin:0 0 6px 0;"
        />
        <p style="font-size:24px; color:#1e3a8a; margin:6px 0;">
          Join in at mural-patch.com
        </p>
        <p style="font-size:20px; color:#1e3a8a; margin:0;">
          📸 @muralpatch
        </p>
      </div>
    `;
    wrapper.appendChild(footer);

    document.body.appendChild(wrapper);

    // ✅ SCALE LOGIC — Apply AFTER rendering to prevent cropping
    await new Promise((r) => setTimeout(r, 250));
    const muralRect = muralClone.getBoundingClientRect();
    const wrapperRect = muralWrapper.getBoundingClientRect();

    const scaleX = wrapperRect.width / muralRect.width;
    const scaleY = wrapperRect.height / muralRect.height;
    const finalScale = Math.min(scaleX, scaleY, 1); // never scale up

    muralClone.style.transform = `scale(${finalScale})`;
    muralClone.style.transformOrigin = "center center";

    // ✅ Capture after scaling is applied
    const canvas = await html2canvas(wrapper, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#e6f0ff",
      windowWidth: wrapper.scrollWidth,
      windowHeight: wrapper.scrollHeight,
    });

    wrapper.remove();

    // ✅ Add thin border on PNG export
    const borderThickness = 2;
    const borderedCanvas = document.createElement("canvas");
    borderedCanvas.width = canvas.width + borderThickness * 2;
    borderedCanvas.height = canvas.height + borderThickness * 2;
    const ctx = borderedCanvas.getContext("2d");
    ctx.fillStyle = "#e6f0ff";
    ctx.fillRect(0, 0, borderedCanvas.width, borderedCanvas.height);
    ctx.drawImage(canvas, borderThickness, borderThickness);

    // ✅ Download / Share as PNG
    const blob = await new Promise((res) => borderedCanvas.toBlob(res, "image/png"));
    const file = new File([blob], "mural-progress.png", { type: "image/png" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "Check out my mural progress!",
        text: "Join in at mural-patch.com 🎨",
      });
    } else {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "mural-progress.png";
      link.click();
    }
  } catch (err) {
    console.error("⚠️ Share progress failed:", err);
  }
};




const handleShareFinalMural = async () => {
  try {
    const muralElement = document.getElementById("mural-container");
    if (!muralElement) return alert("❌ Mural not found");

    // ✅ Clone mural
    const clone = muralElement.cloneNode(true);
    clone.id = "share-final-preview";
    clone.style.border = "6px solid #1e3a8a";
    clone.style.padding = "3px";
    clone.style.margin = "0 auto";
    clone.style.backgroundColor = "white";
    clone.style.maxWidth = "92%";
    clone.style.height = "auto";
    clone.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)";

    // ✅ Outer wrapper (9:16)
    const wrapper = document.createElement("div");
    wrapper.style.width = "1080px";
    wrapper.style.height = "1920px";
    wrapper.style.backgroundColor = "#e6f0ff";
    wrapper.style.display = "flex";
    wrapper.style.flexDirection = "column";
    wrapper.style.alignItems = "center";
    wrapper.style.justifyContent = "center";
    wrapper.style.padding = "125px 30px 30px"; // top, sides, bottom
    wrapper.style.boxSizing = "border-box";
    wrapper.style.textAlign = "center";
    wrapper.style.fontFamily = "sans-serif";
    wrapper.style.border = "2px solid #1e3a8a";

    // 🧩 Header
    const header = document.createElement("div");
    header.innerHTML = `
      <div style="text-align:center; line-height:1.3; margin-top:0px; margin-bottom:16x;">
        <p style="font-size:50px; font-weight:bold; color:#1e3a8a; margin-bottom:2px;">
          Mural is complete! ${new Set(
            squares.filter((sq) => sq.submittedBy).map((sq) => sq.submittedBy)
          ).size} artists contributed!
        </p>
        ${
          answer
            ? `<p style="font-size:42px; font-weight:bold; color:black; margin-top:0; margin-bottom:5px;">
                 Answer: ${answer}
               </p>`
            : ""
        }
        <p style="font-size:30px; color:#333; margin-top:4px; margin-bottom:10px;">
          Clue: ${clue || ""}
        </p>
      </div>
    `;
    wrapper.appendChild(header);

    // ✅ Add white mat + mural
// ✅ Add mural directly (no white mat)
clone.style.border = "6px solid #1e3a8a"; // keep same blue frame as gallery
clone.style.margin = "0 auto 5px auto";
wrapper.appendChild(clone);


    // ✅ Build artist list
    const artistsWithCounts = squares
      .filter((sq) => sq.submittedBy)
      .reduce((acc, sq) => {
        acc[sq.submittedBy] = (acc[sq.submittedBy] || 0) + 1;
        return acc;
      }, {});

    const artistEntries = await Promise.all(
      Object.entries(artistsWithCounts).map(async ([uid, count]) => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          const username = snap.exists() ? snap.data().username : "Unknown";
          return { username, count };
        } catch {
          return { username: "Unknown", count };
        }
      })
    );

    const sortedArtists = artistEntries.sort((a, b) => b.count - a.count);
    const artistsText = sortedArtists
      .map((a) => `${a.username} (${a.count})`)
      .join(", ");

    const artistSection = document.createElement("div");
    artistSection.innerHTML = `

      <p style="font-size:30px; margin:0 10px 20px 10px; color:#333;">
        <strong>Artists:</strong> ${artistsText}
      </p>
    `;
    wrapper.appendChild(artistSection);

    // ✅ Footer
    const footer = document.createElement("div");
    footer.innerHTML = `
      <div style="text-align:center; margin-top:12px; margin-bottom:1px; line-height:1.4;">
        <img 
          src="https://firebasestorage.googleapis.com/v0/b/community-squares-766d6.firebasestorage.app/o/footer%2F46C9DCB8-6230-4B21-8F24-001B0F4C3CA9.PNG?alt=media&token=aade42b3-a937-4cf2-adf1-09fb926fdb36" 
          alt="Mural Patch" 
          style="width:60px; margin:0;"
        />
        <p style="font-size:20px; color:#1e3a8a; margin-top:1px; margin-bottom:1px">
          Join in at mural-patch.com
        </p>
        <p style="font-size:16px; color:#1e3a8a; margin:0;">
          📸 @muralpatch
        </p>
      </div>
    `;
    wrapper.appendChild(footer);

    // ✅ Append temporarily to DOM (fix for iframe/cloned context)
    document.body.appendChild(wrapper);

    // Wait until all images are loaded
    const imgs = wrapper.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise((res) => {
            if (img.complete && img.naturalHeight !== 0) res();
            else img.onload = img.onerror = res;
          })
      )
    );

    // ✅ Capture canvas
    const canvas = await html2canvas(wrapper, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#e6f0ff",
      windowWidth: wrapper.scrollWidth,
      windowHeight: wrapper.scrollHeight,
    });

    // ✅ Remove temporary wrapper
    wrapper.remove();

    // 🟦 Add thin blue outer border
    const scale = 2;
    const borderThickness = 1 * scale;
    const borderedCanvas = document.createElement("canvas");
    borderedCanvas.width = canvas.width + borderThickness * 2;
    borderedCanvas.height = canvas.height + borderThickness * 2;
    const ctx = borderedCanvas.getContext("2d");
    ctx.fillStyle = "#e6f0ff";
    ctx.fillRect(0, 0, borderedCanvas.width, borderedCanvas.height);
    ctx.lineWidth = borderThickness;
    ctx.strokeStyle = "#1e3a8a";
    ctx.strokeRect(
      borderThickness / 2,
      borderThickness / 2,
      borderedCanvas.width - borderThickness,
      borderedCanvas.height - borderThickness
    );
    ctx.drawImage(canvas, borderThickness, borderThickness);

    // ✅ Share or download
    const blob = await new Promise((res) =>
      borderedCanvas.toBlob(res, "image/png")
    );
    const file = new File([blob], "final-mural.png", { type: "image/png" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "Check out our finished mural!",
        text: "Created with mural-patch.com 🎨",
      });
    } else {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "final-mural.png";
      link.click();
    }
  } catch (err) {
    console.error("⚠️ Share final mural failed:", err);
  }
};

// === SETTINGS YOU CAN EDIT ===
const ALLOW_COLOR = true; // 🔁 change to false for black-only mod

function hexToRgb(hex) {
  const bigint = parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r},${g},${b}`;
}

// state and refs
const [showPicker, setShowPicker] = useState(false);
const pickerRef = useRef(null);

useEffect(() => {
  function handleClickOutside(event) {
    if (pickerRef.current && !pickerRef.current.contains(event.target)) {
      setShowPicker(false);
    }
  }

  document.addEventListener("click", handleClickOutside, true); // ✅ use click, not mousedown/touchstart
  return () => {
    document.removeEventListener("click", handleClickOutside, true);
  };
}, []);



return (
    <div style={{ padding: "10px" }}>
      <h1 style={{ marginBottom: "2px", marginTop: "0px" }}>Mural Patch</h1>
      <p
        style={{
          marginTop: "0px",
          fontSize: "14px",
        }}
      >
        a community mural, one patch at a time
      </p>


{/* Instructions */}
<div style={{ marginBottom: "10px" }}>
  <p className="instruction-bubble">
    Click "claim" and have fun painting your patch! <u>Don’t worry about making it perfect</u> — quick and creative is the goal. 
    You'll have 4 hours to submit your patch before it resets.

    <span className="line-spacer"></span>

    Tips: Drawing on a touchscreen is easiest. Using the same browser helps keep your username and track your contributions. Tap any completed patch to see the artist's username below.<br/>And one more thing - don't close your tab while painting or your work will be lost!
    <span className="line-spacer1"></span>
    <strong>Mural Clue: </strong>{clue}
  </p>
</div>


      {/* Clue section 
<div style={{ marginBottom: "2px", fontSize: "14px" }}>
  <h2
    style={{
      display: "inline",
      margin: "0 4px 0 0",
      fontSize: "14px",
      fontWeight: "bold",
    }}
  >
    Clue:
  </h2>
  <p
    style={{
      display: "inline",
      margin: "0",
      fontSize: "14px",
    }}
  >
    {clue}
  </p>
</div>*/}

      {/* Completion message */}
{squares.length > 0 && squares.every((sq) => sq.uploadedDrawingUrl) && (
  <div style={{ marginTop: "0px", textAlign: "center" }}>
    {/* 🎉 Completion message with contributor count */}
    <p style={{ fontSize: "16px", fontWeight: "bold", color: "#1e3a8a",marginBottom:"2px"}}>
      🎉 Woo! Mural is complete —{" "}
      {
        new Set(
          squares
            .filter((sq) => sq.submittedBy)
            .map((sq) => sq.submittedBy)
        ).size
      }{" "}
      artists contributed!
    </p>

    {answer && (
      <p style={{ marginTop: "0px", marginBottom:"10px",fontSize: "16px", color: "black" , fontWeight: "bold"}}>
        Answer: {answer}
      </p>
    )}

    {/* 🔹 Admin-only Save Button */}
    {isAdmin && (
      <button
        onClick={saveMuralToGallery}
        style={{
          marginTop: "10px",
          padding: "0.5rem 1rem",
          backgroundColor: "#16a34a",
          color: "white",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        💾 Save to Gallery
      </button>
    )}
  </div>
)}

{isAdmin && (
  <div style={{ marginTop: "10px",textAlign: "left", marginBottom: "10px" }}>
    <button
      onClick={() => {
        localStorage.removeItem("isAdmin");
        setIsAdmin(false);
        alert("👋 Logged out of admin mode.");
      }}
      style={{
        backgroundColor: "#dc2626", // red
        color: "white",
        border: "none",
        borderRadius: "6px",
        padding: "4px 10px",
        fontSize: "12px",
        cursor: "pointer",
      }}
    >
      Log Out of Admin
    </button>
  </div>
)}

{/* 🟦 Mural completion progress bar */}
{squares.length > 0 && !squares.every((sq) => sq.uploadedDrawingUrl) && (
  <div
    style={{
      margin: "10px 0 15px 0",
      textAlign: "center",
      fontFamily: "sans-serif",
    }}
  >
    {(() => {
      const completed = squares.filter((sq) => sq.uploadedDrawingUrl).length;
      const percent = Math.round((completed / squares.length) * 100);
      return (
        <>
          <p
            style={{
              margin: "0 0 4px 0",
              fontSize: "14px",
              color: "#2563eb",
              fontWeight: "bold",
            }}
          >
            {percent}% of the mural is complete — help finish it!
          </p>

          {/* Outer track (gray with blue border) */}
          <div
            style={{
              width: "20%",
              height: "12px",
              backgroundColor: "#e5e7eb", // light gray background
              border: "1px solid #2563eb", // blue border

              borderRadius: "6px",
              margin: "4px auto 0px auto", // 👈 small top/bottom margins
              padding: "1px", // keeps progress bar from touching edges
              boxSizing: "border-box",
            }}
          >
            {/* Inner progress (blue fill) */}
            <div
              style={{
                width: `${percent}%`,
                height: "100%",
                backgroundColor: "#2563eb",
                borderRadius: "4px",
                transition: "width 0.3s ease-in-out",
              }}
            ></div>
          </div>
        </>
      );
    })()}
  </div>
)}

<div style={{ textAlign: "center", marginTop: "4x",marginBottom: "6px" }}>
  {userHasSubmitted && !isMuralComplete && (
    <button
      onClick={handleShareMyProgress}
      style={{
        backgroundColor: "#2563eb",
        color: "white",
        border: "none",
        borderRadius: "8px",
        padding: "4px 6px",
        marginRight: "8px",
        fontSize: "0.7rem",
        cursor: "pointer",
        opacity: 0.75,
      }}
    >
      📸 Share Progress
    </button>
  )}

  {isMuralComplete && (
    <button
      onClick={handleShareFinalMural}
      style={{
        backgroundColor: "#16a34a",
        color: "white",
        border: "none",
        borderRadius: "10px",
        padding: "8px 12px",
        cursor: "pointer",
      }}
    >
      🖼️ Share Final Mural
    </button>
  )}
</div>



<div
    id="mural-container"
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
      gap: "1px",
      width: "fit-content",
      marginTop: "0px",
      marginBottom: "4px",
      margin: "0 auto",
      overflowX: "hidden",
    }}
  >
    {squares.map((sq) => {
      const locked =
        claimedSquare && claimedSquare.id !== sq.id && !sq.uploadedDrawingUrl;

        const borderColor =
          sq.claimed || sq.uploadedDrawingUrl
            ? "rgba(146, 191, 74, 1)"   // green
            : "rgba(220,220,220,0.6)";  // even lighter unclaimed squares

      return (
        <div
          key={sq.id}
          data-square-id={sq.id}
          style={{
            boxSizing: "border-box",
            border: `0px solid ${borderColor}`,
            aspectRatio: "1 / 1",
            position: "relative",
            backgroundColor: sq.uploadedDrawingUrl ? "white" : "#ecebdeff",
            pointerEvents: locked ? "none" : "auto",
            opacity: locked ? 0.6 : 1,
          }}
            >
              {/* Completed square */}
{sq.uploadedDrawingUrl ? (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",

    // ✨ Tiny square-shaped mask for slight transparency at edges
maskImage: `linear-gradient(
              to top,
              rgba(0,0,0,0.8) 0px,
              black 3px,
              black calc(100% - 3px),
              rgba(0,0,0,0.8) 100%
            ),
            linear-gradient(
              to right,
              rgba(0,0,0,0.8) 0px,
              black 3px,
              black calc(100% - 3px),
              rgba(0,0,0,0.8) 100%
            )`,
WebkitMaskImage: `linear-gradient(
                    to top,
                    rgba(0,0,0,0.8) 0px,
                    black 3px,
                    black calc(100% - 3px),
                    rgba(0,0,0,0.8) 100%
                  ),
                  linear-gradient(
                    to right,
                    rgba(0,0,0,0.8) 0px,
                    black 3px,
                    black calc(100% - 3px),
                    rgba(0,0,0,0.8) 100%
                  )`,
maskComposite: "intersect",
WebkitMaskComposite: "source-in",



    }}
  >
<img
  src={sq.uploadedDrawingUrl}
  alt={`Square ${sq.id}`}
  crossOrigin="anonymous"
  data-square-id={sq.id}
  className={`square-img ${
    !isAnimating || visibleSquares.includes(sq.id) ? "visible" : ""
  }`}
  style={{
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",

  }}
  onClick={async () => {
    if (!sq.submittedBy) {
      return setSubmittedBy(
        `Unknown (row ${sq.row + 1}, col ${sq.col + 1})`
      );
    }
    const snap = await getDoc(doc(db, "users", sq.submittedBy));
    const uname = snap.exists() ? snap.data().username : "Unknown";
    setSubmittedBy(`${uname} (row ${sq.row + 1}, col ${sq.col + 1})`);
  }}
/>

<div
  style={{
    position: "absolute",
    inset: 0,
    pointerEvents: "none",

    // overall strength of vignette
    opacity: 0.1, //

    backgroundImage: `linear-gradient(
                        to top,
                        rgba(0,0,0,0.18),
                        rgba(0,0,0,0) 18%,
                        rgba(0,0,0,0) 88%,
                        rgba(0,0,0,0.18)
                      ),
                      linear-gradient(
                        to right,
                        rgba(0,0,0,0.18),
                        rgba(0,0,0,0) 18%,
                        rgba(0,0,0,0) 88%,
                        rgba(0,0,0,0.18)
                      )`,
    mixBlendMode: "multiply",
  }}
/>

  </div>
) : sq.claimed && sq.claimedBy !== uid ? (
  <p style={{ fontSize: "10px", textAlign: "center" }}>Claimed</p>
) : !sq.claimed && !claimedSquare ? (
  <button
    style={{ width: "100%", height: "100%" }}
    onClick={() => claimSquare(sq.id)}
  >
                  Claim
                </button>
              ) : sq.claimedBy === uid ? (
                <div style={{ textAlign: "center", fontSize: "10px" }}>
                  Yours
                  {timeLeft && (
                    <div style={{ fontSize: "10px", color: "red" }}>{timeLeft}</div>
                  )}
                </div>
              ) : null}

              {/* Admin reset button */}
              {isAdmin && (
                <button
                  onClick={() => confirmAndResetSquare(sq.id)}
                  style={{
                    position: "absolute",
                    bottom: "2px",
                    right: "2px",
                    fontSize: "8px",
                    padding: "2px",
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          );
        })}
        
      </div>

      {/* Submitted by */}
      {submittedBy && (
        <div style={{ marginTop: "4px", textAlign: "center", fontSize: "12px", marginBottom:"2px" }}>
          Submitted by: <strong>{submittedBy}</strong>
        </div>
      )}
      
          {/* Artists summary */}
    {leaderboard.length > 0 && (
      <div style={{ marginTop: "0px", marginBottom:"0px",fontSize: "14px", textAlign: "center" }}>
        <p>
          <strong>Artists:</strong>{" "}
          {leaderboard.map((entry, idx) => (
            <span key={idx}>
              {entry.username} ({entry.count})
              {idx < leaderboard.length - 1 ? ", " : ""}
            </span>
          ))}
        </p>
      </div>
    )}

{squares.every((sq) => sq.uploadedDrawingUrl) && (
  <button
    onClick={playAnimation}
    style={{
      marginTop: "0",
      padding: "8px 12px",
      backgroundColor: "#2563eb",
      color: "white",
      borderRadius: "10px",
      fontSize: "12px",
      border: "none",
      cursor: "pointer",
      alignItems: "center",
    }}
  >
    Play Animation
  </button>
)}

      {/* Drawing panel */}
      {claimedSquare && !claimedSquare.uploadedDrawingUrl && (
        <div
          style={{
            marginTop: "12px",
            maxWidth: "500px",
            marginInline: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
<h3
  style={{
    textAlign: "center",
    marginBottom: "3px",   // ⬅ tighter space before the reference image
    lineHeight: "1.1",
    fontWeight: "normal",
  }}
>
  <span style={{ display: "block", fontSize: "18px", fontWeight: "bold" }}>
    Your Patch
  </span>
  <span
    style={{
      display: "block",
      color: "#2563eb",
      fontSize: "14px",
      fontWeight: "300", // ⬅ lighter font weight
      opacity: 0.8,      // ⬅ slightly faded look
    }}
  >
    (paint it in white grid, have fun with it!😊)
  </span>
</h3>



{/* === Reference + Canvas Wrapper === */}
<div
  style={{
    display: "flex",
    flexDirection: window.innerWidth < 768 ? "column" : "row",
    alignItems: "center",
    justifyContent: "center",
    gap: window.innerWidth < 768 ? "6px" : "12px", // smaller gap on mobile
    width: "100%",
  }}
>
  {/* === Reference === */}
  <div
    style={{
      position: "relative",
      flex: "1 1 auto",
      width: window.innerWidth < 768 ? "72vw" : "280px",
      maxWidth: "320px",
      aspectRatio: "1 / 1",
      marginBottom: "0px",
    }}
  >
    <img
      src={claimedSquare.originalPieceUrl}
      alt="Reference"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        border: "2px solid black",
        boxSizing: "border-box",
      }}
    />
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 200 200"
      preserveAspectRatio="none"
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
    >
      <line x1="100" y1="0" x2="100" y2="200" stroke="rgba(0,0,0,0.2)" />
      <line x1="0" y1="100" x2="200" y2="100" stroke="rgba(0,0,0,0.2)" />
      <line x1="50" y1="0" x2="50" y2="200" stroke="rgba(0,0,0,0.1)" />
      <line x1="150" y1="0" x2="150" y2="200" stroke="rgba(0,0,0,0.1)" />
      <line x1="0" y1="50" x2="200" y2="50" stroke="rgba(0,0,0,0.1)" />
      <line x1="0" y1="150" x2="200" y2="150" stroke="rgba(0,0,0,0.1)" />
    </svg>
  </div>

  {/* === Canvas === */}
  <div
    style={{
      position: "relative",
      flex: "1 1 auto",
      width: window.innerWidth < 768 ? "72vw" : "280px",
      maxWidth: "320px",
      aspectRatio: "1 / 1",
      marginTop: "0px",
    }}
  >
<CanvasDraw
  ref={canvasRef}
  brushColor={
    isErasing
      ? "#ffffff"
      : ALLOW_COLOR
      ? `rgba(${hexToRgb(brushColor)}, ${Math.max(0.05, darkness - softness * 0.1)
})`
      : `rgba(0,0,0,${Math.max(0.05, darkness - softness * 0.1)
})`
  }
  brushRadius={brushRadius}
  lazyRadius={0} // keep responsive
  hideGrid
  style={{
    width: "100%",
    height: "100%",
    display: "block",
    background: "white",
    border: "2px solid black",
    boxSizing: "border-box",
  }}
/>

    <svg
      width="100%"
      height="100%"
      viewBox="0 0 200 200"
      preserveAspectRatio="none"
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
    >
      <line x1="100" y1="0" x2="100" y2="200" stroke="rgba(0,0,0,0.2)" />
      <line x1="0" y1="100" x2="200" y2="100" stroke="rgba(0,0,0,0.2)" />
      <line x1="50" y1="0" x2="50" y2="200" stroke="rgba(0,0,0,0.1)" />
      <line x1="150" y1="0" x2="150" y2="200" stroke="rgba(0,0,0,0.1)" />
      <line x1="0" y1="50" x2="200" y2="50" stroke="rgba(0,0,0,0.1)" />
      <line x1="0" y1="150" x2="200" y2="150" stroke="rgba(0,0,0,0.1)" />
    </svg>
  </div>
</div>




 
{/* === Sliders + Color Picker === */}
<div
  style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "6px",
    marginTop: "10px",
    flexWrap: "wrap",
    width: "100%",
  }}
>
  {/* Size */}
  <label style={{ fontSize: "14px", textAlign: "center" }}>
    Size
    <input
      type="range"
      min="1"
      max="30"
      value={brushRadius}
      onChange={(e) => setBrushRadius(parseInt(e.target.value))}
      style={{
        width: window.innerWidth < 600 ? "24vw" : "100px",
        marginLeft: "4px",
      }}
    />
  </label>

{/* Color */}
{ALLOW_COLOR && (
  <div
    ref={pickerRef}
    style={{
      position: "relative",
      textAlign: "center",
    }}
  >
    <label style={{ fontSize: "14px" }}>Color</label>

    {/* Color square button */}
    <div
      onClick={() => setShowPicker(!showPicker)}
      style={{
        display: "inline-block",
        width: "20px",
        height: "20px",
        marginLeft: "6px",
        borderRadius: "4px",
        backgroundColor: brushColor,
        border: "1px solid #999",
        cursor: "pointer",
        verticalAlign: "middle",
      }}
    ></div>

    {/* Dropdown picker */}
    {showPicker && (
      <div
        style={{
          position: "absolute",
          bottom: "30px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          padding: "12px 14px 14px 14px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Grid color picker – NO sliders */}
        <div
          style={{
            width: window.innerWidth < 600 ? "140px" : "200px",
            height: window.innerWidth < 600 ? "140px" : "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              transform:
                window.innerWidth < 600 ? "scale(0.8)" : "scale(0.95)",
              transformOrigin: "center center",
              width: "200px",  // HexColorPicker native size
              height: "200px",
            }}
          >
          <div className="no-hue-picker">
            <HexColorPicker
              color={brushColor}
              onChange={(newColor) => setBrushColor(newColor)}
            />
          </div>

          </div>
        </div>

        {/* Preset colors row */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 10,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {PRESET_COLORS.map((color, idx) => (
            <button
              key={color}
              onClick={() => {
                setSelectedPresetIndex(idx);
                setBrushColor(color);
              }}
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                border:
                  selectedPresetIndex === idx
                    ? "2px solid #000"
                    : "1px solid #ccc",
                padding: 0,
                background: color,
                cursor: "pointer",
                outline: "none",
              }}
            />
          ))}
        </div>
      </div>
    )}
  </div>
)}


  {/* Opacity */}
  <label style={{ fontSize: "14px", textAlign: "center" }}>
    Opacity
    <input
      type="range"
      min="0.01"
      max="1"
      step="0.01"
      value={darkness}
      onChange={(e) => setDarkness(parseFloat(e.target.value))}
      style={{
        width: window.innerWidth < 600 ? "24vw" : "100px",
        marginLeft: "4px",
      }}
    />
  </label>
</div>

    



{/* === Buttons === */}
<div style={{ display: "flex", gap: "6px", marginTop: "10px" }}>
  <button onClick={() => setIsErasing(!isErasing)} style={{ fontSize: "14px" }}>
    {isErasing ? "Eraser On" : "Eraser Off"}
  </button>
  <button onClick={() => canvasRef.current.undo()} style={{ fontSize: "14px" }}>
    Undo
  </button>
</div>


                      {/* ✅ Username Greeting / Prompt */}
<div style={{ marginTop: "10px",marginBottom: "10px", textAlign: "left" }}>
  {username ? (
    <p style={{ fontSize: "14px",marginTop: "0px",marginBottom: "0px", }}>
      👋 Hey there, <strong>{username}</strong>!
      <br />
      {/* <button
        onClick={() => {
          setUsername("");
          setPendingUsername("");
        }}
        style={{ fontSize: "8px", marginTop: "2px" }}
      >
        Change Username
      </button>*/}
    </p>
  ) : (
    <div>
      <label style={{ fontSize: "12px" }}>
        Username:{" "}
        <input
          type="text"
          value={pendingUsername}
          onChange={(e) => setPendingUsername(e.target.value)}
          style={{ marginLeft: "2px", fontSize: "12px" ,width:"60px"}}
        />
      </label>
      <button
        onClick={async () => {
          if (!uid || !pendingUsername) return;
          await setDoc(doc(db, "users", uid), { username: pendingUsername }, { merge: true });
          setUsername(pendingUsername);
          alert("✅ Username saved!");
        }}
        style={{ fontSize: "10px", marginLeft: "5px" }}
      >
        Save Username
      </button>
    </div>
  )}
</div>

          {/* Submit + username (kept for convenience here too) */}
          <div style={{ marginTop: "0px" }}>
            <button
              onClick={() => submitCanvasDrawing(claimedSquare.id)}
              style={{ fontSize: "14px", marginBottom: "5px" }}
            >
              <strong>Submit Drawing</strong>
            </button>
          </div>
        </div>
      )}

      {/* ===== Footer ===== */}
      <footer style={{ marginTop: "40px", textAlign: "center" }}>
        <a
          href="https://www.instagram.com/muralpatch?igsh=bnhrdTkwMjI5dmhy&utm_source=qr"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            textDecoration: "none",
            color: "#4B5563",
            fontSize: "10px",
          }}
        >
          <span>Instagram:</span>
          <img
            src="https://firebasestorage.googleapis.com/v0/b/community-squares-766d6.firebasestorage.app/o/footer%2F46C9DCB8-6230-4B21-8F24-001B0F4C3CA9.PNG?alt=media&token=aade42b3-a937-4cf2-adf1-09fb926fdb36"
            alt="Instagram"
            style={{ width: "50px", height: "50px" }}
          />
        </a>
      </footer>

      {/* Admin uploader */}
      {isAdmin && <MuralUploader />}
    </div>
  );
}

export default MuralPage;
